import { useCallback, useState } from 'react';

interface FieldPendingEntry {
  pending: boolean;
  optimisticValue: unknown;
}

/**
 * Per-field "in flight" tracking with an immediate optimistic display value.
 *
 * Without this, a control whose displayed value is derived purely from
 * `mtObjList` (or `globalSettings`) snaps back to the old value the instant
 * the user picks a new one — the confirmed/pending value doesn't exist until
 * the send's promise resolves and the pending-MT registration runs, but React
 * re-renders with the old value on every state change in between (e.g. the
 * `pending` spinner flag flipping true), and a controlled `<select>`/toggle
 * follows whatever `value=`/`enabled=` prop it's given right then.
 *
 * `run()` sets an optimistic value synchronously (visible on the very next
 * render, before any network round trip), then clears it once the async work
 * settles — by then the mtObjList-derived pending value (registered via
 * `usePendingMTTracking`'s `registerSentMT`, called from inside that same
 * async work) should already be live, so the handoff is seamless. Callers
 * compose `getOptimisticValue(key) ?? <mtObjList-derived-or-current-value>`.
 */
export function useFieldPending() {
  const [state, setState] = useState<Record<string, FieldPendingEntry>>({});

  const isPending = useCallback((key: string) => !!state[key]?.pending, [state]);

  const getOptimisticValue = useCallback(
    <T,>(key: string): T | undefined => (state[key]?.pending ? (state[key].optimisticValue as T) : undefined),
    [state],
  );

  const run = useCallback(async (key: string, optimisticValue: unknown, fn: () => Promise<unknown>) => {
    setState((s) => ({ ...s, [key]: { pending: true, optimisticValue } }));
    try {
      await fn();
    } finally {
      setState((s) => {
        if (!(key in s)) return s;
        const next = { ...s };
        delete next[key];
        return next;
      });
    }
  }, []);

  return { isPending, getOptimisticValue, run };
}
